import { getMenuAuth } from "src/apis/authApi";
import {  useCallback, useEffect } from "react";
import {  getPRD3030_R01List,   getPRD3030_R01AllCount, getPRD3030_R01LotList } from "./Api_PRD3030_R01"
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import useMenuGlobalStore from "src/store/useMenuStore";
import { downloadExcel } from "src/utils/blob/blobUtil";
import { exportPRD3030_R01Excel } from "./Api_PRD3030_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

import { usePRD3030_R01Store, defaultPRD3030_R01Filter, PRD3030_R01Filter, ExtendedPRD3030_R01Dto } from "./Store_PRD3030_R01";
import { PRD3030_R01Dto } from "./Dto_PRD3030_R01";
import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent";


interface ProductResultContentProps {
    setMessage: (message: string) => void;
}

export const Content_PRD3030_R01 = (props: ProductResultContentProps) => {
    const {
		setMessage,
    } = props;

    // 전역 store에서 menuCd 가져오기
	const { 
		PRD3030_R01Filter, setPRD3030_R01Filter,
		PRD3030_R01PastFilter, setPRD3030_R01PastFilter,
		PRD3030_R01List, setPRD3030_R01List, 
		setAllCount,
		setLotList,
		setSelectedProductResultNo,
		setScrollTop,
		setLotScrollTop,
	} = usePRD3030_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { selectedThirdMenu } = useMenuGlobalStore();
    const menuCdStore = selectedThirdMenu?.menuCd;
    const setAuth = useActionbarAuthStore((state) => state.setAuth);

	useFetchActionbarAuth("PRD3030_R01");

    useEffect(() => {
        getMenuAuth("PRD3030_R01").then((res) => {
            setAuth(res.data);
        });
    }, []);

    useEffect(() => {
        if (!menuCdStore) {
            console.log("[ProductResultContent] menuCd가 없습니다. 메뉴를 선택하세요.");
        } else {
            console.log("[ProductResultContent] menuCd:" + menuCdStore);
        }
    }, [menuCdStore]);

	const getParams = (filter: Partial<PRD3030_R01Filter>) => {
		return {
			compCd: filter.compCd,
			productDateRange: filter.productDateRange,
			orderType: filter.orderType,
			workplaceCd: filter.workplaceCd,
			itemCd: filter.itemCd,
			brandCd: filter.brandCd
		};
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getPRD3030_R01AllCount(getParams(PRD3030_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [PRD3030_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setLoadingSpinner(true);
        setScrollTop(null);
        setLotScrollTop(null);
        try {

            const response = await getPRD3030_R01List(
                '',
                getParams(PRD3030_R01Filter),
                PRD3030_R01List.length ?? 0
            );

            // response.data.data를 PRD3040_R01Dto[]로 타입 캐스팅
            const rawData = response.data.data as PRD3030_R01Dto[];
            
            // isSubtotal 속성을 먼저 추가하여 ExtendedPRD3040_R01Dto[]로 변환
            const extendedRawData: ExtendedPRD3030_R01Dto[] = rawData.map(item => ({
                ...item,
                isSubtotal: false
            }));

			const newList = [...extendedRawData ] as ExtendedPRD3030_R01Dto[];
			setPRD3030_R01List(newList);

            const processAllCount = await renewalAllCount();

            if (newList && newList.length > 0) {
                setPRD3030_R01PastFilter(PRD3030_R01Filter);
                setPRD3030_R01List(newList);
                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
            } else {
                showWarning('데이터가 없습니다.');
                setPRD3030_R01List([]);
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setPRD3030_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
    }, [PRD3030_R01Filter, setPRD3030_R01List, setPRD3030_R01PastFilter, setScrollTop, setLotScrollTop]);
    
    const handleExcelClick = async () => {
        const {isDefaultFilter, isDefaultPastFilter, listLength} = fetchFilterConfig(
            PRD3030_R01Filter,
            PRD3030_R01PastFilter,
            defaultPRD3030_R01Filter,
            PRD3030_R01List.length
        );

        const response = await exportPRD3030_R01Excel(
            isDefaultFilter? '' : 'search',
            getParams(PRD3030_R01Filter),
            listLength
        );
        const binaryString = atob(response.data.data);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
        // Blob 생성
        const blob = new Blob([byteArray]);
        downloadExcel(blob, '생산실적 조회-excel.xlsx');
    };

    // LOT 조회 함수
    const fetchLotList = useCallback(async (productResultNo: string) => {
        setLotScrollTop(null);
        if (!productResultNo) {
            setLotList([]);
            setSelectedProductResultNo('');
            return;
        }

        try {
            const response = await getPRD3030_R01LotList(productResultNo);
            setLotList(response.data.data);
            setSelectedProductResultNo(productResultNo);
        } catch (error) {
            console.error('LOT 조회 실패:', error);
            showWarning('LOT 조회에 실패했습니다.');
            setLotList([]);
            setSelectedProductResultNo('');
        }
    }, [setLotList, setSelectedProductResultNo, setLotScrollTop]);


    return {
        fetchData,
        handleExcelClick,
        fetchLotList,
    };
};
